/**
 * Boot sequence: mount the pre-game warning dialog (`./scare/warning`), and
 * on Start — the user gesture — unlock audio and request the camera in that
 * same click handler. `requestCamera` races `getUserMedia()` against a
 * `CAMERA_TIMEOUT_MS` timeout (see `./camera`) — a webcam already held by another app
 * (OBS's own face-tracker, another browser tab) makes `getUserMedia()` hang rather than
 * reject, and without this race the Start click would await forever and the game would
 * never begin. A timeout, denial, or absence all fall back to mouse-steering with a
 * banner naming the likely cause (`./camera`'s `cameraBannerFor`) and arm a
 * `CAMERA_RETRY_KEY` listener that tears the whole session down and restarts it fresh —
 * simplest correct way to hand a late-acquired stream to a from-scratch `startGameLoop`
 * without teaching it to swap input modes mid-run. Either way `startGameLoop` then takes
 * over. Each session's DOM (board/canvas/scare overlay) lives under its own `.scare-session`
 * wrapper so a retry can cleanly remove the old one before mounting a new one; the
 * returned `GameLoopHandle` is kept and `stop()`-ped on `pagehide` so the camera
 * stream/landmarker/RAF loop don't keep running after the page is gone.
 *
 * URL params (documented in this package's CLAUDE.md, parsed once here via
 * `./params`): `?delegate=gpu` is passed through to `./face/landmarker`;
 * `?reducedMotion=1` forces the static/no-strobe scare on top of whatever the
 * warning dialog's checkbox says (an operator override, one-directional — it
 * can only turn reduced motion ON); `?audioUrl=` swaps in a custom scream
 * clip for `ScareScene` — same-origin only (see `./url-guard`'s
 * `sameOriginUrl`), content boundary is original-assets-only so no
 * third-party remote media is loaded even as an operator drop-in;
 * `?scareThreshold=`/`?deadzone=`/`?gain=`/`?minCutoff=`/`?invertX=`/
 * `?invertY=` are read here and passed straight through to `startGameLoop`,
 * which applies them in the gaze pipeline (see `./game`).
 */

import { CAMERA_TIMEOUT_MS, cameraBannerFor, isCameraRetryKey } from "./camera";
import { type GameLoopHandle, startGameLoop } from "./game";
import { parseParams } from "./params";
import { resolveArtBaseUrl } from "./render/art";
// `__SCARYMAZE_ART_BASE_URL__` below is declared ambiently by ./render/env.d.ts (picked
// up automatically — a `.d.ts` file needs no import) rather than a real module import.
import { renderWarning } from "./scare/warning";
import { sameOriginUrl } from "./url-guard";

/** Vite's `define` (see ../vite.config.ts) substitutes this at build time; under
 * `bun test` (this file is never imported by a test — see ./url-guard's header comment
 * for why) it would read `"undefined"`, which this guard treats the same as "never
 * configured". See client/render/env.d.ts. */
function buildTimeArtBaseUrl(): string | undefined {
  return typeof __SCARYMAZE_ART_BASE_URL__ !== "undefined" ? __SCARYMAZE_ART_BASE_URL__ : undefined;
}

const CAMERA_CONSTRAINTS: MediaStreamConstraints = {
  video: { width: 640, height: 480, facingMode: "user" },
};

/** The level-3 payoff scare video — see `public/scare/ASSETS.md` and this package's
 * README for the one-line copy step that restores it on a fresh clone. Vite publishes
 * `public/` to `dist/`'s root regardless of the build's relative `base`, and the server
 * always serves this app at `/`, so a root-absolute path is safe here. Missing (a fresh
 * clone that hasn't run the copy step yet) degrades to the built-in scare — see
 * `ScareScene.armedMode`/`resolveArmedMode`, not a broken level 3. */
const SCARE_VIDEO_URL = "/scare/secret_ending.mp4";

function mountAppRoot(): HTMLElement {
  const app = document.getElementById("app");
  if (app === null) throw new Error("main: #app root missing from index.html");
  return app;
}

function mountLiveRegion(root: HTMLElement): HTMLElement {
  const region = document.createElement("div");
  region.className = "sr-live";
  region.setAttribute("role", "status");
  region.setAttribute("aria-live", "polite");
  root.appendChild(region);
  return region;
}

function createAudioContext(): AudioContext | null {
  try {
    return new AudioContext();
  } catch {
    return null; // No WebAudio support — the scream just won't play.
  }
}

function timeoutAfter(ms: number): Promise<"timeout"> {
  return new Promise((resolve) => {
    setTimeout(() => resolve("timeout"), ms);
  });
}

/**
 * Races `getUserMedia()` against `CAMERA_TIMEOUT_MS`. A timeout doesn't cancel the
 * underlying request — `getUserMedia()` has no `AbortSignal` support to speak of here —
 * so if it resolves later with a live stream nobody asked for anymore, that stream's
 * tracks are stopped as soon as it arrives rather than left recording with nothing
 * consuming it.
 */
async function requestCamera(): Promise<{ stream: MediaStream | null; banner: string | null }> {
  if (navigator.mediaDevices?.getUserMedia === undefined) {
    return { stream: null, banner: cameraBannerFor("unsupported") };
  }

  const attempt = navigator.mediaDevices.getUserMedia(CAMERA_CONSTRAINTS).then(
    (stream): { kind: "granted"; stream: MediaStream } => ({ kind: "granted", stream }),
    (): { kind: "denied" } => ({ kind: "denied" }),
  );

  const raced = await Promise.race([
    attempt,
    timeoutAfter(CAMERA_TIMEOUT_MS).then((kind) => ({ kind }) as const),
  ]);

  if (raced.kind === "granted") {
    return { stream: raced.stream, banner: null };
  }
  if (raced.kind === "timeout") {
    void attempt.then((late) => {
      if (late.kind === "granted") {
        for (const track of late.stream.getTracks()) track.stop();
      }
    });
  }
  return { stream: null, banner: cameraBannerFor(raced.kind) };
}

function resolveAudioUrl(raw: string | undefined): string | undefined {
  if (raw === undefined) return undefined;
  const safe = sameOriginUrl(raw, window.location.href);
  if (safe === null) {
    console.warn("scarymaze: ignoring cross-origin ?audioUrl=", raw);
    return undefined;
  }
  return safe;
}

interface AcceptResult {
  readonly handle: GameLoopHandle;
  readonly cameraGranted: boolean;
}

async function onAccept(
  appRoot: HTMLElement,
  liveRegion: HTMLElement,
  reducedMotion: boolean,
): Promise<AcceptResult> {
  for (const el of appRoot.querySelectorAll(".scare-warning")) el.remove();
  for (const el of appRoot.querySelectorAll(".scare-session")) el.remove();

  const sessionRoot = document.createElement("div");
  sessionRoot.className = "scare-session";
  appRoot.appendChild(sessionRoot);

  const audio = createAudioContext();
  const { stream, banner: cameraBanner } = await requestCamera();

  const params = parseParams(window.location.search);
  const effectiveReducedMotion = reducedMotion || params.reducedMotion;
  const audioUrl = resolveAudioUrl(params.audioUrl);
  const artBaseUrl = resolveArtBaseUrl({
    artBaseParam: params.artBase,
    buildTimeBaseUrl: buildTimeArtBaseUrl(),
  });

  const handle = startGameLoop({
    root: sessionRoot,
    liveRegion,
    reducedMotion: effectiveReducedMotion,
    audio,
    stream,
    cameraBanner,
    artBaseUrl,
    audioUrl,
    videoUrl: SCARE_VIDEO_URL,
    params,
  });
  return { handle, cameraGranted: stream !== null };
}

function boot(): void {
  const root = mountAppRoot();
  const liveRegion = mountLiveRegion(root);
  let gameLoopHandle: GameLoopHandle | null = null;
  let retryArmed = false;

  // Stop the camera stream/landmarker/RAF loop when the page is being
  // unloaded or backgrounded persistently (bfcache-friendly, unlike
  // 'beforeunload'/'unload').
  window.addEventListener("pagehide", () => {
    gameLoopHandle?.stop();
  });

  function startSession(reducedMotion: boolean): void {
    void onAccept(root, liveRegion, reducedMotion)
      .then(({ handle, cameraGranted }) => {
        gameLoopHandle = handle;
        if (!cameraGranted) armCameraRetry(reducedMotion);
      })
      .catch((err: unknown) => {
        console.error("scarymaze: failed to start after Start click", err);
      });
  }

  /** A single keydown listener for `CAMERA_RETRY_KEY` that tears the current session
   * down and restarts it from scratch (see this file's header for why a full restart,
   * not an in-place mode swap). Re-arms itself automatically if the retry itself still
   * doesn't get a camera (startSession calls back in here via the same `!cameraGranted`
   * check). Idempotent — a second arm attempt while one is already listening is a no-op. */
  function armCameraRetry(reducedMotion: boolean): void {
    if (retryArmed) return;
    retryArmed = true;
    const onKeydown = (ev: KeyboardEvent): void => {
      if (!isCameraRetryKey(ev.key)) return;
      window.removeEventListener("keydown", onKeydown);
      retryArmed = false;
      gameLoopHandle?.stop();
      startSession(reducedMotion);
    };
    window.addEventListener("keydown", onKeydown);
  }

  renderWarning(root, startSession);
}

boot();
